import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrgId } from '@/hooks/auth';
import { useCurrentUser } from '@/hooks/auth';

export type CallOutcome =
  | 'connected'           // Conexão feita
  | 'meeting_scheduled'   // Reunião agendada
  | 'callback_later'      // Retornar depois
  | 'no_answer'           // Não atendeu
  | 'refused';            // Recusada manualmente

export interface CreateIncomingActivityInput {
  /** Telefone do que está ligando (formato livre — gravado em custom_data). */
  phone: string;
  phoneNormalized: string;
  /** Vínculos detectados pelo lookup. */
  leadId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
}

export interface CompleteIncomingActivityInput {
  activityId: string;
  outcome: CallOutcome;
  notes?: string;
}

const OUTCOME_LABEL: Record<CallOutcome, string> = {
  connected: 'Conexão feita',
  meeting_scheduled: 'Reunião agendada',
  callback_later: 'Retornar depois',
  no_answer: 'Não atendeu',
  refused: 'Chamada recusada',
};

/**
 * Cria uma activity tipo `ligacao` em status `pending` no momento que a
 * chamada toca. A ideia é que o usuário possa registrar o resultado depois
 * via `useCompleteIncomingCallActivity`.
 */
export function useCreateIncomingCallActivity() {
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateIncomingActivityInput) => {
      if (!orgId) throw new Error('Org não definida');
      if (!user) throw new Error('Usuário não autenticado');

      const now = new Date();
      const insertData = {
        org_id: orgId,
        type: 'ligacao',
        activity_type_key: 'ligacao',
        title: `Chamada recebida de ${input.phone}`,
        date: now.toISOString(),
        status: 'pending',
        lead_id: input.leadId ?? null,
        deal_id: input.dealId ?? null,
        company_id: input.companyId ?? null,
        created_by: user.user_id,
        owner_id: user.user_id,
        custom_data: {
          source: 'sonax_incoming',
          phone: input.phone,
          phone_normalized: input.phoneNormalized,
          ringing_at: now.toISOString(),
        },
      };

      const { data, error } = await supabase
        .from('activities')
        .insert(insertData)
        .select('id')
        .single();
      if (error) throw error;

      return data.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
    onError: (err: Error) => {
      console.warn('[useCreateIncomingCallActivity] erro:', err.message);
    },
  });
}

/**
 * Atualiza a activity criada por `useCreateIncomingCallActivity` com o
 * resultado escolhido pelo usuário e (opcionalmente) notas.
 */
export function useCompleteIncomingCallActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CompleteIncomingActivityInput) => {
      const { activityId, outcome, notes } = input;
      const completedAt = new Date().toISOString();

      // Lê custom_data atual pra preservar campos
      const { data: existing } = await supabase
        .from('activities')
        .select('custom_data, description')
        .eq('id', activityId)
        .maybeSingle();

      const prevCustom = (existing?.custom_data as Record<string, unknown>) ?? {};
      const newCustom = {
        ...prevCustom,
        outcome,
        outcome_label: OUTCOME_LABEL[outcome],
        completed_at: completedAt,
      };

      const { error } = await supabase
        .from('activities')
        .update({
          status: 'done',
          description: notes && notes.trim().length > 0
            ? notes.trim()
            : existing?.description ?? null,
          custom_data: newCustom,
        })
        .eq('id', activityId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
    onError: (err: Error) => {
      console.warn('[useCompleteIncomingCallActivity] erro:', err.message);
    },
  });
}
